// TODO: CHAIR-19112 Keep or remove this file and usages after test finishes
import AuthenticationView from 'chairisher/view/authentication';

import createDomElement from 'chairisher/util/dom';

import { getQuickCreateUrl, isAuthenticated } from 'chairisher/context/auth';
import { getAmplitudeExperimentFlagMap } from 'chairisher/context/experiments';
import { getGoogleSigninClientId, isGoogleSigninSdkLoaded, setGoogleSigninSdkLoaded } from 'chairisher/context/google';
import { isExperimentVariantB } from 'chairisher/thirdparty/experiments';
import { AmplitudeEventProperty, logAmplitudeEvent } from 'chairisher/util/analytics';
import { submitFormWithRecaptcha } from 'chairisher/view/helper/recaptcha';
import { isTosAccepted, renderDefaultErrorOnTosField } from 'chairisher/view/helper/tosfield';

const GOOGLE_SIGNIN_JSSDK_SCRIPT_ID = 'gmail-jssdk';

let loginOrCreateForm = null;

const googleLoginCallback = (response) => {
    if (loginOrCreateForm) {
        loginOrCreateForm.querySelector('[name="google_token"]').value = response.credential;
        loginOrCreateForm.querySelector('[name="is_provider_initial_attempt"]').value = 1;
        submitFormWithRecaptcha(loginOrCreateForm);
    }
};

/**
 * Initializes the Google SDK
 * @see https://developers.google.com/identity/gsi/web/reference/js-reference#IdConfiguration
 */
function initGoogleSDK() {
    try {
        window.google.accounts.id.initialize({
            client_id: getGoogleSigninClientId(),
            ux_mode: 'popup',
            callback: googleLoginCallback,
        });
        setGoogleSigninSdkLoaded(true);
    } catch (e) {
        setGoogleSigninSdkLoaded(false);
    }
}

/**
 * Renders the Google Sign-in button which should only be called after the SDK has been initialized
 */
function renderSignInButton() {
    const buttonDiv = document.querySelector('.js-google-sign-in-wrapper');
    window.google.accounts.id.renderButton(buttonDiv, {
        logo_alignment: 'center',
        shape: 'rectangular',
        text: 'continue_with',
        theme: 'outline',
    });
}

/**
 * @returns {boolean} True indicates the path is eligible to have Google One Tap displayed
 */
function isPathEligibleForOneTapPrompt() {
    const blacklistedPaths = ['^/account/*', '^/cart', '^/email/create/*', '/offer/create$', '^/question/*'];

    const re = new RegExp(blacklistedPaths.join('|'), 'i');
    return re.test(window.location.pathname) === false;
}

/**
 * Renders the Google One Tap prompt which should only be called after the SDK has been initialized
 */
function renderOneTapPrompt() {
    if (isPathEligibleForOneTapPrompt() && !isAuthenticated()) {
        // get a hidden email form to submit
        const authView = new AuthenticationView({
            modalSelector: '#js-hidden-auth-modal',
        });
        authView
            .display(
                {
                    url: getQuickCreateUrl('google-one-tap'),
                },
                false,
            )
            .done(() => {
                loginOrCreateForm = document.querySelector(`${authView.getModalSelector()} form`);
                window.google.accounts.id.prompt();

                // // On submit, log in the user and then register an amplitude event for existing accounts
                loginOrCreateForm.addEventListener('submit', (e) => {
                    e.preventDefault();

                    authView.submit(
                        loginOrCreateForm.getAttribute('action'),
                        new URLSearchParams(new FormData(loginOrCreateForm)).toString(),
                        (successData) => {
                            if (!successData.was_account_created) {
                                logAmplitudeEvent('account - login', {
                                    [AmplitudeEventProperty.LOGIN_TYPE]: successData.login_type,
                                });
                            }
                        },
                    );
                });
            });
    }
}

/**
 * Asynchronously loads Google SDK and renders the button or one tap prompt on load
 * @param {boolean} isButton Optional indication of if button or one tap prompt should be rendered
 */
function injectGoogleSignInSdkScript(isButton) {
    if (!document.getElementById(GOOGLE_SIGNIN_JSSDK_SCRIPT_ID) || !isGoogleSigninSdkLoaded()) {
        const firstScript = document.getElementsByTagName('script')[0];
        const googleSigninScript = createDomElement('script', {
            attributes: {
                async: true,
                id: GOOGLE_SIGNIN_JSSDK_SCRIPT_ID,
                src: 'https://accounts.google.com/gsi/client',
            },
        });
        googleSigninScript.onload = () => {
            initGoogleSDK();
            if (isButton) {
                renderSignInButton();
            } else {
                renderOneTapPrompt();
            }
        };
        firstScript.parentNode.insertBefore(googleSigninScript, firstScript);
    } else if (isButton && !document.querySelector('.js-google-sign-in-wrapper').querySelector('div[role="button"]')) {
        renderSignInButton();
    }
}

/**
 * Binds Google Sign-in buttons with event listeners
 */
function bindGoogleSigninButtons() {
    // select all unbound Google sign-in buttons
    const googleBtns = document.querySelectorAll('.js-btn-google-sign-in:not(.js-is-bound)');

    googleBtns.forEach((button) => {
        const btn = button;
        btn.disabled = true;

        btn.addEventListener('click', ({ currentTarget: clickedBtn }) => {
            loginOrCreateForm = clickedBtn.closest('form');

            if (isTosAccepted(loginOrCreateForm)) {
                const submitBtn = loginOrCreateForm.querySelector('button[type="submit"]');
                submitBtn.disabled = true;

                document.querySelector('.js-google-sign-in-wrapper').querySelector('div[role="button"]').click();
            } else {
                renderDefaultErrorOnTosField(loginOrCreateForm);
            }
        });

        // re-enable the newly bound buttons
        btn.disabled = false;

        // prevent double binding through exclusion
        btn.classList.add('js-is-bound');
    });
}

/**
 * Entrypoint for Google Sign-in button where SDK gets initialized, and button or one tap prompt is rendered with handlers if in treatment
 * @param {boolean} isButton Optional indication of if button or one tap prompt should be rendered
 */
export default function renderGoogleSignin(isButton = true) {
    if (isExperimentVariantB(getAmplitudeExperimentFlagMap().GMAIL_SOCIAL_LOGIN)) {
        injectGoogleSignInSdkScript(isButton);

        if (isButton) {
            bindGoogleSigninButtons();
            // show the button
            document.querySelectorAll('.js-btn-google-sign-in-container').forEach((btnContainer) => {
                btnContainer.classList.remove('hidden');
            });
        }
    }
}
